import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { doc, getDoc as nativeGetDoc } from 'firebase/firestore';
import ErrorMessage from '../formComponents/ErrorMessage';
import SubmitButton from '../formComponents/SubmitButton';
import '../../../index.css';
import '../../../styles/forms.css';
import { db, functions } from '../../../firebase-client';
import { httpsCallable } from 'firebase/functions';
import { extractPricingOverrideTagNames, getDeletedTagNames } from '../../teams/utils/tagHelper';
import { extractSystemManagedTags } from '../../teams/utils/learnToRow';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const createEmptyAgeRule = () => ({
  day: 1,
  month: 'September',
  rule: 'over',
  age: 10,
  tagName: ''
});
const editorStateFromTags = (initialTags = []) => {
  const grouped = {};
  const rules = [];
  (initialTags || []).forEach(tag => {
    if (tag?.systemManaged === true) return;
    if (tag.type === 'ageRule') {
      rules.push({
        day: tag.effectiveDate?.day ?? 1,
        month: tag.effectiveDate?.month ?? 'September',
        rule: tag.rule ?? 'over',
        age: Number.isFinite(Number(tag.age)) ? Number(tag.age) : 10,
        tagName: (tag.tagName || tag.tagNames?.[0] || '').toString()
      });
      return;
    }
    const cat = tag.category || 'Uncategorised';
    if (!grouped[cat]) {
      grouped[cat] = {
        category: cat,
        multiAssignable: tag.multiAssignable || false,
        visibleToMembers: tag.visibleToMembers ?? true,
        selfAssignable: tag.selfAssignable || false,
        requiredForMembers: tag.requiredForMembers === true,
        tags: []
      };
    }
    grouped[cat].requiredForMembers = grouped[cat].requiredForMembers || tag.requiredForMembers === true;
    if (tag.requiredForMembers === true) {
      grouped[cat].visibleToMembers = true;
      grouped[cat].selfAssignable = true;
    }
    grouped[cat].tags.push(tag.name);
  });
  return { groups: Object.values(grouped), ageTagRules: rules };
};
const snapshotEditorState = (groups, ageTagRules) => JSON.stringify({ groups, ageTagRules });
const canAutosave = (groups, ageTagRules) => groups.every(group => (
  group.category.trim() !== ''
  && group.tags.length > 0
  && group.tags.every(tag => typeof tag === 'string' && tag.trim() !== '')
)) && ageTagRules.every(rule => (rule.tagName || '').trim() !== '');
const tagsForSave = (groups, ageTagRules, systemManagedTags) => {
  const flatTags = groups.flatMap(group => group.tags.map(tagName => ({
    name: tagName,
    category: group.category,
    selfAssignable: group.requiredForMembers ? true : group.selfAssignable,
    multiAssignable: group.multiAssignable,
    visibleToMembers: group.requiredForMembers ? true : group.visibleToMembers,
    requiredForMembers: group.requiredForMembers === true
  })));
  ageTagRules.map(rule => ({
    ...rule,
    tagName: (rule.tagName || '').trim()
  })).filter(rule => rule.tagName).forEach(rule => {
    flatTags.push({
      type: 'ageRule',
      effectiveDate: { day: rule.day, month: rule.month },
      rule: rule.rule,
      age: rule.age,
      tagName: rule.tagName,
      tagNames: [rule.tagName]
    });
  });
  systemManagedTags.forEach(tag => flatTags.push(tag));
  return flatTags;
};
function InlineHelp({
  text,
  ariaLabel
}) {
  const [open, setOpen] = useState(false);
  return <span style={{
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  }}>
      <button type="button" onClick={() => setOpen(prev => !prev)} aria-label={ariaLabel} aria-expanded={open} title={open ? 'Hide help' : 'Show help'} style={{
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      fontSize: '0.95rem',
      lineHeight: 1,
      padding: 0
    }}>
        ❔
      </button>
      {open && <span className="text-muted" style={{
      marginTop: 4,
      fontSize: '0.85rem',
      lineHeight: 1.35
    }}>
          {text}
        </span>}
    </span>;
}
export default function EditTagsForm({
  initialTags = [],
  isOpen,
  onClose,
  onSave,
  teamId,
  onUnsavedChangesChange = () => {},
  dataAccess = null,
}) {
  const { getDoc } = dataAccess || { getDoc: nativeGetDoc };
  const initialEditorState = useMemo(() => editorStateFromTags(initialTags), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [groups, setGroups] = useState(initialEditorState.groups);
  const [ageTagRules, setAgeTagRules] = useState(initialEditorState.ageTagRules);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState('saved');
  const [saveCycle, setSaveCycle] = useState(0);
  const baselineRef = useRef(snapshotEditorState(initialEditorState.groups, initialEditorState.ageTagRules));
  const latestSnapshotRef = useRef(baselineRef.current);
  const savingRef = useRef(false);
  const savedTagsRef = useRef(initialTags);
  const systemManagedTagsRef = useRef(extractSystemManagedTags(initialTags));
  const currentSnapshot = useMemo(() => snapshotEditorState(groups, ageTagRules), [groups, ageTagRules]);
  const isDirty = currentSnapshot !== baselineRef.current;
  latestSnapshotRef.current = currentSnapshot;
  useEffect(() => {
    onUnsavedChangesChange(isDirty);
    return () => onUnsavedChangesChange(false);
  }, [isDirty, onUnsavedChangesChange]);
  const handleAddGroup = () => {
    setGroups(prev => [...prev, {
      category: '',
      multiAssignable: false,
      visibleToMembers: true,
      selfAssignable: false,
      requiredForMembers: false,
      tags: []
    }]);
  };
  const handleRemoveGroup = index => {
    const group = groups[index];
    if (!window.confirm(`Delete the "${group?.category || 'untitled'}" tag group and all of its tag options when you save?`)) return;
    setGroups(groups.filter((_, i) => i !== index));
  };
  const handleGroupChange = (index, key, value) => {
    const copy = [...groups];
    copy[index][key] = value;
    if (key === 'requiredForMembers' && value === true) {
      copy[index].visibleToMembers = true;
      copy[index].selfAssignable = true;
    }
    if ((key === 'visibleToMembers' || key === 'selfAssignable') && value === false) {
      copy[index].requiredForMembers = false;
    }
    setGroups(copy);
  };
  const handleAddTag = index => {
    const copy = [...groups];
    copy[index].tags.push('');
    setGroups(copy);
  };
  const handleTagChange = (groupIndex, tagIndex, value) => {
    const copy = [...groups];
    copy[groupIndex].tags[tagIndex] = value;
    setGroups(copy);
  };
  const handleRemoveTag = (groupIndex, tagIndex) => {
    const tagName = groups[groupIndex]?.tags?.[tagIndex] || 'this tag option';
    if (!window.confirm(`Remove "${tagName}" when you save? Existing member assignments and linked pricing may be affected.`)) return;
    const copy = [...groups];
    copy[groupIndex].tags.splice(tagIndex, 1);
    setGroups(copy);
  };
  const handleAddAgeRule = () => {
    setAgeTagRules(prev => [...prev, createEmptyAgeRule()]);
  };
  const handleAgeRuleChange = (index, key, value) => {
    setAgeTagRules(prev => prev.map((rule, i) => i === index ? {
      ...rule,
      [key]: value
    } : rule));
  };
  const handleRemoveAgeRule = index => {
    if (!window.confirm('Remove this age-based tagging rule when you save?')) return;
    setAgeTagRules(prev => prev.filter((_, i) => i !== index));
  };
  const saveChanges = async ({ automatic = false } = {}) => {
    if (savingRef.current) return false;
    const groupsSnapshot = groups;
    const rulesSnapshot = ageTagRules;
    const editorSnapshot = snapshotEditorState(groupsSnapshot, rulesSnapshot);
    if (automatic && !canAutosave(groupsSnapshot, rulesSnapshot)) {
      setAutosaveStatus('incomplete');
      return false;
    }
    savingRef.current = true;
    if (!automatic) setSubmitting(true);
    setAutosaveStatus('saving');
    setError(null);
    try {
      const flatTags = tagsForSave(groupsSnapshot, rulesSnapshot, systemManagedTagsRef.current);
      const deletedTagNames = getDeletedTagNames(savedTagsRef.current, flatTags);
      if (deletedTagNames.length > 0) {
        let pricedDeletedTagNames = [];
        let pricingLookupFailed = false;
        try {
          const [stripeSetupSnap, teamSnap] = await Promise.all([getDoc(doc(db, 'teams', teamId, 'stripe', 'setup')), getDoc(doc(db, 'teams', teamId))]);
          const pricingOverrideTagNames = extractPricingOverrideTagNames({
            stripeSetup: stripeSetupSnap.exists() ? stripeSetupSnap.data() || {} : {},
            subscriptions: teamSnap.exists() ? teamSnap.data()?.subscriptions || {} : {}
          });
          pricedDeletedTagNames = deletedTagNames.filter(name => pricingOverrideTagNames.has(name));
        } catch (pricingErr) {
          pricingLookupFailed = true;
        }
        if (pricedDeletedTagNames.length > 0) {
          const plural = pricedDeletedTagNames.length > 1;
          const confirmed = window.confirm(`Deleting ${plural ? 'these tags' : 'this tag'} will remove ${plural ? 'their' : 'its'} pricing overrides (monthly and yearly): ${pricedDeletedTagNames.join(', ')}.\n\nContinue?`);
          if (!confirmed) {
            setAutosaveStatus('pending');
            return false;
          }
        } else if (pricingLookupFailed) {
          const confirmed = window.confirm('Could not verify pricing overrides right now. Deleting tags may remove linked monthly/yearly pricing overrides. Continue?');
          if (!confirmed) {
            setAutosaveStatus('pending');
            return false;
          }
        }
      }
      await onSave(flatTags, { keepOpen: automatic });
      savedTagsRef.current = flatTags;
      baselineRef.current = editorSnapshot;

      // Immediately apply the age rule if present
      if (flatTags.some(tag => tag.type === 'ageRule')) {
        try {
          if (!dataAccess?.platform) await httpsCallable(functions, 'applyAgeBasedTagNow')({
            teamId
          });
        } catch {
          // swallow; not fatal to saving tags
        }
      }
      setAutosaveStatus(latestSnapshotRef.current === editorSnapshot ? 'saved' : 'pending');
      return true;
    } catch (err) {
      setError(err.message || 'Failed to save tags.');
      setAutosaveStatus('error');
      return false;
    } finally {
      savingRef.current = false;
      if (latestSnapshotRef.current !== editorSnapshot) {
        setSaveCycle(cycle => cycle + 1);
      }
      if (!automatic) setSubmitting(false);
    }
  };
  const handleSubmit = async e => {
    e.preventDefault();
    const saved = await saveChanges();
    if (saved) onClose({ discardUnsavedChanges: true });
  };
  useEffect(() => {
    if (!isOpen || !isDirty || savingRef.current) return undefined;
    setAutosaveStatus(canAutosave(groups, ageTagRules) ? 'pending' : 'incomplete');
    if (!canAutosave(groups, ageTagRules)) return undefined;
    const timer = window.setTimeout(() => saveChanges({ automatic: true }), 1200);
    return () => window.clearTimeout(timer);
  }, [ageTagRules, currentSnapshot, groups, isDirty, isOpen, saveCycle]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!isOpen) return null;
  const Toggle = ({
    id,
    label,
    helpText,
    checked,
    onChange,
    disabled = false
  }) => <div className="toggle-container" style={{
    justifyContent: 'space-between',
    marginRight: 0,
    gap: '0.75rem',
    width: '100%'
  }}>
      <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6
    }}>
        <label htmlFor={id} style={{
        marginBottom: 0,
        cursor: 'pointer'
      }}>{label}</label>
        <InlineHelp text={helpText} ariaLabel={`${label} help`} />
      </span>
      <label htmlFor={id} style={{
      display: 'inline-flex',
      alignItems: 'center',
      cursor: 'pointer'
    }}>
        <input id={id} type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} />
        <span className="toggle-slider" />
      </label>
    </div>;
  return <>
      <h2 className="modal-title">Edit Tags</h2>
      {error && <ErrorMessage message={error} />}

      <form onSubmit={handleSubmit} className="modal-form space-y-4">
        {groups.map((group, i) => <fieldset key={i} className="tag-group">
            <legend>{group.category || 'Untitled Tag Group'}</legend>

            <div className="form-group">
              <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 4
          }}>
                <label style={{
              marginBottom: 0
            }}>Tag Group Name (Category)</label>
                <InlineHelp ariaLabel="Tag group name help" text="This is the family name for a set of tags. It is the category that holds the group." />
              </div>
              <input type="text" value={group.category} onChange={e => handleGroupChange(i, 'category', e.target.value)} disabled={submitting} className="input" placeholder="e.g. Membership Level" />
            </div>

            <div className="form-group checkbox-row" style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8
        }}>
              <Toggle id={`tag-group-${i}-multi`} label="Allow multiple tags in this group" helpText="Turn on if a member can hold more than one tag in this group." checked={group.multiAssignable} onChange={val => handleGroupChange(i, 'multiAssignable', val)} />
              <Toggle id={`tag-group-${i}-visible`} label="Show this group to members" helpText="Turn off to keep this group admin-only." checked={group.requiredForMembers ? true : group.visibleToMembers} disabled={group.requiredForMembers === true} onChange={val => handleGroupChange(i, 'visibleToMembers', val)} />
              <Toggle id={`tag-group-${i}-self`} label="Let members self-assign tags in this group" helpText="Turn on if members should be able to choose tags for themselves." checked={group.requiredForMembers ? true : group.selfAssignable} disabled={group.requiredForMembers === true} onChange={val => handleGroupChange(i, 'selfAssignable', val)} />
              <Toggle id={`tag-group-${i}-required`} label="Members must choose a tag in this group" helpText="Turn on to require members to choose at least one tag in this group when joining or editing their tags." checked={group.requiredForMembers === true} onChange={val => handleGroupChange(i, 'requiredForMembers', val)} />
            </div>

            <div className="form-group">
              <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 4
          }}>
                <label style={{
              marginBottom: 0
            }}>Tag Options in This Group</label>
                <InlineHelp ariaLabel="Tag options help" text="These are the actual tags assigned to members inside this category." />
              </div>
              {group.category.trim() !== '' && group.tags.some(tag => typeof tag === 'string' && tag.trim().toLowerCase() === group.category.trim().toLowerCase()) && <p className="text-muted" style={{
            marginBottom: 8,
            fontSize: '0.9rem'
          }}>
                  This group and one tag are both named {`"${group.category.trim()}"`}. Consider a broader group name.
                </p>}
              {group.tags.map((tag, j) => <div key={j} className="form-inline">
                  <input type="text" value={tag} onChange={e => handleTagChange(i, j, e.target.value)} className="input" placeholder="e.g. Senior" disabled={submitting} required />

                  <SubmitButton type="button" label="Remove" onClick={() => handleRemoveTag(i, j)} disabled={submitting} variant="ghost" size="xs" />
                </div>)}

              <div className="tag-actions" style={{
            display: 'flex',
            gap: 8,
            marginTop: 8
          }}>
                <SubmitButton type="button" label="Add Tag Option" onClick={() => handleAddTag(i)} disabled={submitting} variant="secondary" size="sm" />
                <SubmitButton type="button" label="Delete Tag Group" onClick={() => handleRemoveGroup(i)} disabled={submitting} variant="ghost" size="sm" />
              </div>
            </div>
          </fieldset>)}

        <SubmitButton type="button" label="Add Tag Group" onClick={handleAddGroup} disabled={submitting} variant="secondary" size="sm" />

        <fieldset className="tag-group">
          <legend>Age-Based Tag Rules</legend>

          {ageTagRules.length === 0 && <p className="text-muted">No age rules yet. Add one to auto-tag by age category.</p>}

          {ageTagRules.map((rule, index) => <div key={index} className="form-group" style={{
          marginBottom: 16
        }}>
              <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
                <label style={{
              marginBottom: 0
            }}>Rule {index + 1}</label>
                <SubmitButton type="button" label="Remove Rule" onClick={() => handleRemoveAgeRule(index)} disabled={submitting} variant="ghost" size="sm" />
              </div>

              <label style={{
            marginTop: 8,
            display: 'block'
          }}>Effective Date</label>
              <div className="form-inline" style={{
            gap: 8
          }}>
                <select value={rule.day} onChange={e => handleAgeRuleChange(index, 'day', parseInt(e.target.value, 10))} disabled={submitting}>
                  {[...Array(31)].map((_, n) => <option key={n} value={n + 1}>
                      {n + 1}
                    </option>)}
                </select>
                <select value={rule.month} onChange={e => handleAgeRuleChange(index, 'month', e.target.value)} disabled={submitting}>
                  {MONTHS.map(m => <option key={m} value={m}>
                      {m}
                    </option>)}
                </select>
              </div>

              <label style={{
            marginTop: 8,
            display: 'block'
          }}>Rule</label>
              <div className="form-inline" style={{
            gap: 8
          }}>
                <select value={rule.rule} onChange={e => handleAgeRuleChange(index, 'rule', e.target.value)} disabled={submitting}>
                  <option value="over">Over</option>
                  <option value="under">Under</option>
                </select>
                <input type="number" value={rule.age} onChange={e => handleAgeRuleChange(index, 'age', parseInt(e.target.value || '0', 10))} className="input" min={0} disabled={submitting} />
              </div>

              <label style={{
            marginTop: 8,
            display: 'block'
          }}>Tag to Assign</label>
              <input type="text" value={rule.tagName} onChange={e => handleAgeRuleChange(index, 'tagName', e.target.value)} className="input" disabled={submitting} required />
            </div>)}

          <SubmitButton type="button" label="Add Age-Based Tag Rule" onClick={handleAddAgeRule} disabled={submitting} variant="secondary" size="sm" />
        </fieldset>

        <div className="form-actions">
          <span className="text-muted" role="status" aria-live="polite" style={{ fontSize: '0.875rem', marginRight: 'auto' }}>
            {autosaveStatus === 'saving' ? 'Saving changes…' : autosaveStatus === 'pending' ? 'Changes save automatically' : autosaveStatus === 'incomplete' ? 'Finish the empty fields to autosave' : autosaveStatus === 'error' ? 'Changes not saved — retrying after your next edit' : 'All changes saved'}
          </span>
          <div className="form-actions-right">
            <SubmitButton type="button" label="Close" onClick={onClose} disabled={submitting} variant="ghost" size="sm" />
            <SubmitButton type="submit" label={submitting || autosaveStatus === 'saving' ? 'Saving…' : 'Save'} disabled={submitting || autosaveStatus === 'saving'} variant="primary" />
          </div>
        </div>
      </form>
    </>;
}
EditTagsForm.propTypes = {
  initialTags: PropTypes.array.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  teamId: PropTypes.string.isRequired,
  onUnsavedChangesChange: PropTypes.func
};
